import { memo, useState, useCallback } from 'react';
import { cn, ButtonTabs, List, useMedias, RawHtml } from '@divlab/divanui';

import useTranslation from '@Queries/useTranslation';
import DisposalPriceTable from '@Components/DisposalPriceTable';
import styles from './WithTable.module.css';

import type { TableData } from '../../typings';
import type { FC, HTMLAttributes } from 'react';
import type { Tab } from '@divlab/divanui';

export interface WithTableProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  table: TableData;
  additionalCost?: string[];
}

const WithTable: FC<WithTableProps> = (props) => {
  const { table, additionalCost, ...restProps } = props;
  const { t } = useTranslation();
  const { isDesktop, isMobile } = useMedias();

  const [currentTab, setCurrentTab] = useState('delivery');
  const title =
    table.tabs.length === 1 && table.tabs[0].id === 'delivery' ? t('price-delivery') : t('price');

  const handleChangeTab = useCallback((_e, tab: Tab) => {
    setCurrentTab(tab.id);
  }, []);

  return (
    <div {...restProps}>
      <div className={styles.contentContainer}>
        <div
          className={cn(styles.tabsHeader, { [styles.disposalActive]: currentTab === 'disposal' })}
        >
          <div className={styles.heading}>{title}</div>
          {table.tabs.length > 1 && (
            <ButtonTabs
              className={styles.tabs}
              defaultValue={'delivery'}
              tabs={table.tabs}
              inversed
              onChangeTab={handleChangeTab}
            />
          )}
        </div>
        <div
          className={cn(styles.tableWrapper, {
            [styles.active]: currentTab === 'delivery',
          })}
        >
          {table.delivery && <RawHtml className={styles.table} html={table.delivery} />}

          {table.suburb && <RawHtml className={styles.table} html={table.suburb} />}

          {table.upliftFirstFloor && (
            <RawHtml className={styles.table} html={table.upliftFirstFloor} />
          )}
        </div>

        <div
          className={cn(styles.tableWrapper, {
            [styles.active]: currentTab === 'assembly',
          })}
        >
          {table.assembly && <RawHtml className={styles.table} html={table.assembly} />}
          {table.additional && <RawHtml className={styles.table} html={table.additional} />}
        </div>

        {table.disposal && (
          <div
            className={cn(styles.tableWrapper, {
              [styles.active]: currentTab === 'disposal',
            })}
          >
            <div className={styles.disposalInfo}>{table.disposal.teaser}</div>
            <DisposalPriceTable
              tableData={table.disposal}
              showMobileVariant={isMobile}
              showDesktopVariant={isDesktop}
            />
          </div>
        )}

        {additionalCost?.length > 0 && (
          <List
            className={styles.additionalCost}
            items={additionalCost}
            renderChild={(paragraph: string) => (
              <div className={styles.paragraph}>
                <RawHtml html={paragraph} />
              </div>
            )}
          />
        )}
      </div>
    </div>
  );
};

export default memo(WithTable);
